import { useCallback, useImperativeHandle, useLayoutEffect, useRef, type ReactNode, type Ref } from 'react';
import { styled } from '@mui/material/styles';

import { TOGGLE_EASE, TOGGLE_MOVE_MS } from './ViewToggle';

/**
 * Two things in one place, one per view, cross-fading as the view changes.
 *
 * A phone's header keeps its lockup across both pages and swaps only what sits
 * at its end: Saved and the count on Explore, Share My Schedule on My Schedule.
 * The header itself does not swipe, so the swap follows the page swipe's
 * progress instead — painted imperatively, from the same `onExploreOffset` that
 * drives the view toggle, so the two move together on one curve.
 *
 * Explore's content is in flow and sets the height; My Schedule's is drawn over
 * it. That way the header is the same height in both views, and nothing under
 * it moves as they swap.
 */

export interface CrossfadeHandle {
  /** 0 is the first child, 1 the second. */
  follow: (at: number, ms: number, ease: string) => void;
}

const Stack = styled('div')({
  position: 'relative',
  display: 'flex',
  justifyContent: 'flex-end',
});

const Over = styled('div')({
  position: 'absolute',
  top: '50%',
  right: 0,
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
});

interface Props {
  ref?: Ref<CrossfadeHandle>;
  /** Which of the two is showing: 0 or 1. */
  at: number;
  first: ReactNode;
  second: ReactNode;
}

export default function ViewCrossfade({ ref, at, first, second }: Props) {
  const a = useRef<HTMLDivElement | null>(null);
  const b = useRef<HTMLDivElement | null>(null);
  const painted = useRef<number | null>(null);

  const follow = useCallback((to: number, ms: number, ease: string) => {
    const p = Math.max(0, Math.min(1, to));
    const fade = ms ? `opacity ${ms}ms ${ease}` : 'none';
    if (a.current) {
      a.current.style.transition = fade;
      a.current.style.opacity = String(1 - p);
      // Whichever is fading out stops taking taps at once, so a half-faded
      // Saved pill can never answer a tap meant for Share.
      a.current.style.pointerEvents = p > 0.5 ? 'none' : '';
    }
    if (b.current) {
      b.current.style.transition = fade;
      b.current.style.opacity = String(p);
      b.current.style.pointerEvents = p > 0.5 ? '' : 'none';
    }
    painted.current = p;
  }, []);

  useImperativeHandle(ref, () => ({ follow }), [follow]);

  useLayoutEffect(() => {
    const jump = painted.current === null;
    if (!jump && painted.current === at) return;
    follow(at, jump ? 0 : TOGGLE_MOVE_MS, TOGGLE_EASE);
  }, [at, follow]);

  return (
    <Stack>
      <div ref={a} style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
        {first}
      </div>
      <Over ref={b}>{second}</Over>
    </Stack>
  );
}
